import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import MicRoundedIcon from "@mui/icons-material/MicRounded";
import StopCircleRoundedIcon from "@mui/icons-material/StopCircleRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import RecordVoiceOverRoundedIcon from "@mui/icons-material/RecordVoiceOverRounded";
import KeyboardRoundedIcon from "@mui/icons-material/KeyboardRounded";
import { useOwnerVoiceOperator } from "./OwnerVoiceOperatorContext";

const STATUS_LABEL = {
  idle: "LISTO",
  listening: "ESCUCHANDO",
  processing: "ENTENDIENDO",
  speaking: "HABLANDO",
  saving: "GUARDANDO",
  error: "REVISAR",
};

export default function OwnerVoiceOperatorPanel({ persistent = false }) {
  const voice = useOwnerVoiceOperator();
  const [text, setText] = React.useState("");
  const [showComposer, setShowComposer] = React.useState(false);
  if (!voice) return null;

  const submitText = () => {
    const clean = text.trim();
    if (!clean) return;
    voice.sendText(clean);
    setText("");
  };

  const activeForm = voice.currentConfig?.();
  const listening = voice.isListening;
  const blockedMic = !voice.speechSupported;

  if (persistent) {
    const jarvisText = voice.interimHeard
      ? `Te escucho: ${voice.interimHeard}`
      : (voice.message || "Decime qué querés resolver");
    return (
      <Box sx={{ width: "100%", minWidth: 0, py: .25 }}>
        <Stack direction="row" spacing={.7} alignItems="center" sx={{ minHeight: 50, minWidth: 0 }}>
          <Tooltip title={voice.needsMicPermission ? "Autorizar micrófono" : voice.alwaysOnEnabled ? "Dormir Jarvis" : "Hablar con Jarvis"}>
            <span>
              <IconButton
                onClick={voice.needsMicPermission || !voice.alwaysOnEnabled ? () => voice.enableAlwaysOn({ greet: true }) : voice.disableAlwaysOn}
                disabled={blockedMic}
                aria-label={voice.alwaysOnEnabled ? "Dormir Jarvis" : "Hablar con Jarvis"}
                sx={{
                  width: 44, height: 44, flex: "0 0 auto",
                  bgcolor: voice.isListening ? "success.main" : voice.alwaysOnEnabled ? "primary.main" : "action.hover",
                  color: voice.alwaysOnEnabled ? "primary.contrastText" : "text.primary",
                  boxShadow: voice.isSpeaking || voice.isListening ? "0 0 0 6px rgba(243,90,58,.10)" : "none",
                }}
              >
                {voice.isSpeaking ? <RecordVoiceOverRoundedIcon /> : <MicRoundedIcon />}
              </IconButton>
            </span>
          </Tooltip>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ fontWeight: 1000, fontSize: 11, color: voice.isListening ? "success.main" : "primary.main", textTransform: "uppercase", letterSpacing: ".08em" }}>Jarvis</Box>
            <Box sx={{ mt: .05, fontWeight: 850, fontSize: { xs: 12.5, sm: 14 }, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{jarvisText}</Box>
          </Box>

          <Tooltip title="Escuchar de nuevo"><span><IconButton onClick={voice.readCurrent} disabled={!voice.speakSupported} sx={{ width: 40, height: 40 }}><VolumeUpRoundedIcon /></IconButton></span></Tooltip>
          <Tooltip title={showComposer ? "Ocultar teclado" : "Escribir"}><IconButton onClick={() => setShowComposer((value) => !value)} sx={{ width: 40, height: 40 }}><KeyboardRoundedIcon /></IconButton></Tooltip>
        </Stack>

        {showComposer ? (
          <Stack direction="row" spacing={.6} alignItems="center" sx={{ mt: .45, pl: { sm: "50px" } }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              value={text}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); submitText(); } }}
              placeholder={activeForm ? "Respondé a Jarvis…" : "Escribile a Jarvis…"}
              sx={{ "& .MuiInputBase-root": { minHeight: 40, borderRadius: 2.3, fontSize: 13.5 } }}
            />
            <IconButton onClick={submitText} disabled={!text.trim()} aria-label="Enviar a Jarvis" color="primary" sx={{ width: 42, height: 42 }}><SendRoundedIcon /></IconButton>
          </Stack>
        ) : null}
      </Box>
    );
  }

  return (
    <Box sx={{
      mb: persistent ? .55 : 1,
      p: 1,
      borderRadius: 2.6,
      border: "1px solid rgba(243,90,58,.18)",
      bgcolor: "background.paper",
      boxShadow: "0 10px 30px rgba(20,31,42,.045)",
      backdropFilter: persistent ? "blur(12px)" : undefined,
    }}>
      <Stack direction={{ xs: "column", lg: "row" }} spacing={.7} alignItems={{ lg: "center" }}>
        <Stack direction="row" spacing={.6} alignItems="center" sx={{ minWidth: { lg: 255 } }}>
          <RecordVoiceOverRoundedIcon color="primary" />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={.45} alignItems="center">
              <Chip
                size="small"
                label={voice.needsMicPermission ? "BUCLE · AUTORIZAR MIC" : voice.alwaysOnEnabled ? (voice.handsFree && listening ? "BUCLE · ESCUCHANDO" : "BUCLE · ACTIVO") : (STATUS_LABEL[voice.status] || "LISTO")}
                color={voice.status === "error" || voice.needsMicPermission ? "warning" : voice.alwaysOnEnabled && voice.handsFree && listening ? "success" : "default"}
                sx={{ height: 22, fontSize: 10.5, fontWeight: 1000 }}
              />
              {activeForm ? <Chip size="small" variant="outlined" label={activeForm.title} sx={{ height: 22, maxWidth: 130, fontSize: 10.5, fontWeight: 900 }} /> : null}
            </Stack>
            <Box sx={{ mt: .12, fontWeight: 1000, fontSize: 13.5 }}>Jarvis</Box>
            <Box sx={{ mt: .1, color: "text.secondary", fontWeight: 800, fontSize: 11.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{voice.message || "¿Qué querés hacer?"}</Box>
          </Box>
        </Stack>

        <TextField
          fullWidth
          size="small"
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); submitText(); } }}
          placeholder={activeForm ? "Respondé el dato o comando…" : "Hablale a Jarvis: ‘qué tengo hoy’, ‘anotá que…’, ‘creá una tarea…’"}
        />

        <Stack direction="row" spacing={.35} alignItems="center" flexWrap="wrap" useFlexGap sx={{ flex: "0 0 auto" }}>
          <Tooltip title={blockedMic ? "Reconocimiento de voz no disponible" : voice.needsMicPermission ? "Autorizar micrófono" : voice.alwaysOnEnabled ? "Dormir Jarvis" : "Activar Jarvis permanente"}>
            <span>
              <Button
                onClick={voice.needsMicPermission || !voice.alwaysOnEnabled ? () => voice.enableAlwaysOn({ greet: true }) : voice.disableAlwaysOn}
                disabled={blockedMic}
                color={voice.alwaysOnEnabled && !voice.needsMicPermission ? "error" : "primary"}
                variant={voice.alwaysOnEnabled && !voice.needsMicPermission ? "outlined" : "contained"}
                startIcon={voice.alwaysOnEnabled && !voice.needsMicPermission ? <StopCircleRoundedIcon /> : <MicRoundedIcon />}
                sx={{ minHeight: 38, px: 1.25, textTransform: "none", fontWeight: 1000 }}
              >
                {voice.needsMicPermission ? "Activar micrófono" : voice.alwaysOnEnabled ? "Dormir" : "Activar Jarvis"}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Leer"><span><IconButton onClick={voice.readCurrent} disabled={!voice.speakSupported} sx={{ width: 38, height: 38 }}><VolumeUpRoundedIcon fontSize="small" /></IconButton></span></Tooltip>
          <Button variant="contained" size="small" onClick={submitText} disabled={!text.trim()} endIcon={<SendRoundedIcon />} sx={{ minHeight: 38, minWidth: 82, textTransform: "none", fontWeight: 950 }}>Enviar</Button>
          <TextField
            select
            size="small"
            value={voice.speechRate || 1.5}
            onChange={(event) => voice.changeSpeechRate(event.target.value)}
            sx={{ width: 72, "& .MuiInputBase-root": { height: 38, fontWeight: 900, fontSize: 12 } }}
          >
            <MenuItem value={1.15}>1.15×</MenuItem>
            <MenuItem value={1.3}>1.3×</MenuItem>
            <MenuItem value={1.5}>1.5×</MenuItem>
            <MenuItem value={1.7}>1.7×</MenuItem>
          </TextField>
        </Stack>
      </Stack>

      {voice.interimHeard || voice.lastHeard ? (
        <Box sx={{ mt: .55, pl: { lg: "262px" }, color: voice.interimHeard ? "success.main" : "text.secondary", fontSize: 11.5, fontWeight: 750, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {voice.interimHeard ? `Escuchando: “${voice.interimHeard}”` : `Último: “${voice.lastHeard}”`}
        </Box>
      ) : null}
      {voice.needsMicPermission && voice.speechSupported ? <Alert severity="info" sx={{ mt: .7, py: 0 }}>El modo permanente está preparado. El navegador necesita que autorices el micrófono una vez; después Jarvis se reengancha solo dentro de Owner 360.</Alert> : null}
      {!voice.speechSupported ? <Alert severity="info" sx={{ mt: .7, py: 0 }}>Este navegador no ofrece reconocimiento de voz compatible. Jarvis sigue funcionando por texto y puede hablar si la síntesis de voz está disponible.</Alert> : null}
    </Box>
  );
}
